import { styled } from '@mui/material/styles';
import { Box, Card, CardContent, CardHeader, FormControl, FormControlLabel, FormGroup, Container, Typography, Tooltip } from '@mui/material';
import { commonStyles, styleTokens } from '../assets/styles/commonStyles';

export const StyledMainContainer = styled(Box)(({ theme }) => ({
  ...commonStyles.responsiveFlexContainer(theme),
  backgroundColor: theme.palette.mode === 'light' 
    ? theme.palette.grey[100] 
    : theme.palette.grey[900],
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100vh'
}));

export const StyledContentContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2), // Reduced from 4 to 2
  marginBottom: theme.spacing(4)
}));

export const StyledTabsContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(1)
}));

export const StyledButtonContainer = styled(Container)(({ theme }) => ({
  marginTop: 0,
  marginBottom: theme.spacing(1)
}));

export const StyledPageTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h4.fontSize,
  fontWeight: 600,
  color: theme.palette.text.primary
}));

export const StyledPackageCard = styled(Card)(({ theme }) => ({
  ...commonStyles.enhancedCard(theme),
  ...commonStyles.standardCardDimensions(theme),
  padding: theme.spacing(2),
  overflow: 'hidden' 
}));

export const StyledDeliveryCard = styled(Card)(({ theme }) => ({
  ...commonStyles.enhancedCard(theme),
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    height: 300,
    minHeight: 300
  },
  
  [theme.breakpoints.down('sm')]: {
    height: 'auto',
    minHeight: 200
  }
}));

export const StyledPackageCardContent = styled(CardContent)({
  display: 'flex',
  flex: 1,
  width: '100%',
  overflow: 'hidden'
});

export const StyledDeliveryCardHeader = styled(CardHeader)<{ requestType: string }>(({ theme, requestType }) => ({
  color: requestType === 'LIGHTS_ON' ? theme.palette.grey[500] : theme.palette.text.primary,
  
  '& .MuiCardHeader-title': {
    fontSize: '1.25rem',
    fontWeight: 600,
    
    [theme.breakpoints.down('md')]: {
      fontSize: '1.1rem'
    },
    
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem'
    }
  }
}));

export const StyledPackageCardHeader = styled(CardHeader)(({theme}) => ({
  color: theme.palette.text.primary,
  
  '& .MuiCardHeader-title': {
    fontSize: '1.25rem',
    fontWeight: 600,
    
    [theme.breakpoints.down('md')]: {
      fontSize: '1.1rem'
    },
    
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem'
    }
  }
}));

export const StyledDeliveryCardContent = styled(CardContent)({
  overflow: 'hidden',
  flex: 1
});

export const StyledPackageFormControl = styled(FormControl)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  
  '& .MuiRadioGroup-root': {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    height: '100%',
    gap: theme.spacing(0.5),
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingRight: theme.spacing(2),

    '& .MuiFormControlLabel-root': {
      margin: 0,
      flex: '0 0 auto',
      width: 'calc(50% - 16px)',
      minWidth: 0,
    },
    '& .MuiFormControlLabel-label': {
      flex: 1,
      minWidth: 0,
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
    '& .MuiRadio-root': {
      padding: '5px 9px',
    }
  }
}));

export const StyledPackageTooltip = styled(Tooltip)<{ show: boolean }>(({ show }) => ({
  '& .MuiTooltip-popper': {
    display: show ? 'none' : 'block',
    pointerEvents: 'none'
  }
}));

export const StyledPackageFormControlLabel = styled(FormControlLabel)<{ packageColor?: string }>(({ theme, packageColor }) => ({
  color: packageColor || theme.palette.text.primary,
  marginRight: 0,
  marginBottom: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  margin: theme.spacing(0.25, 1, 0.25, 0.25), 
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(2),
  transition: styleTokens.transitions.default,
  
  '&:hover': {
    backgroundColor: 'rgba(86, 147, 247, 0.1)', 
    transform: 'translateX(8px)' 
  },
  
  '& .MuiFormControlLabel-label': {
    fontSize: '0.95rem',
    fontWeight: 500
  },
  
  '& .MuiRadio-root:hover': {
    backgroundColor: 'transparent', 
    transform: 'scale(1.05)'
  }
}));

export const StyledDeliveryFormGroup = styled(FormGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  alignContent: 'flex-start',
  height: '200px',
  gap: theme.spacing(0.5),
  overflow: 'auto',
  
  // Responsive heights
  [theme.breakpoints.up('sm')]: {
    height: '230px',
  },
  
  [theme.breakpoints.up('md')]: {
    height: '250px',
  },
  
  [theme.breakpoints.up('lg')]: {
    height: '270px',
  },
  
  [theme.breakpoints.up('xl')]: {
    height: '300px',
  },
  
  '& .MuiFormControlLabel-root': {
    marginBottom: 0,
    marginRight: theme.spacing(2),
    flex: '0 0 auto',
    width: 'auto',
    minWidth: '140px',
    
    [theme.breakpoints.up('sm')]: {
      width: 'calc(50% - 32px)',
    },
    
    [theme.breakpoints.up('md')]: {
      width: 'calc(50% - 32px)',
    },
    
    [theme.breakpoints.up('lg')]: {
      width: 'calc(50% - 32px)',
    },
    
    [theme.breakpoints.up('xl')]: {
      width: 'calc(50% - 32px)',
    }
  }
}));

export const StyledDeliveryFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  padding: theme.spacing(0.5),
  margin: theme.spacing(0.25, 1, 0.25, 0.25), 
  borderRadius: theme.spacing(2),
  transition: styleTokens.transitions.default,
  
  '&:hover': {
    backgroundColor: 'rgba(86, 147, 247, 0.1)',
    transform: 'translateX(8px)'
  },
  
  '& .MuiFormControlLabel-label': {
    fontWeight: 500,
    
    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem'
    },
    
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem'
    }
  },
  
  '& .MuiCheckbox-root:hover': {
    backgroundColor: 'transparent', 
    transform: 'scale(1.05)'
  }
}));
